import { NextInteraction } from '@/store/modules/salesChurn/types';
import { translations } from '@/plugins/localisation/';

export default class CalendarInvite {
  start: string;
  end: string;
  summary: string;
  location: string;
  description: string;
  url: string;

  constructor(interaction: NextInteraction, clientName: string | undefined) {
    this.start = this.formatStartTime(
      interaction.dateString,
      interaction.startTime
    );
    this.end = this.formatEndTime(
      interaction.dateString,
      interaction.endTime,
      interaction.startTime
    );
    this.summary = this.createMeetingSummary(clientName);
    this.location = this.createLocation(interaction.type);
    this.description = this.createDescription(interaction.actions);
    this.url = this.createDownloadUrl();
  }

  _formatTime = (date: Date, hour: number, minute: number) => {
    date.setHours(hour, minute);

    // iCalendar standard uses an ISO string type format
    // without dashes, colons, and milliseconds
    return (
      date
        .toISOString()
        .replace(/[-:]/g, '')
        .split('.')[0] + 'Z'
    );
  };

  formatStartTime = (date: string, time: string) => {
    const _date = new Date(date);
    const hour = time.split(':')[0];
    const minute = time.split(':')[1];

    return this._formatTime(_date, parseInt(hour), parseInt(minute));
  };

  formatEndTime = (date: string, time: string, startTime: string) => {
    // If an end time doesn't exist (for migrated interactions), make the end time the start time + 1 hour
    const _time = time === '-' ? startTime : time;
    const endTimeAdjustment = time === '-' ? 1 : 0;

    const _date = new Date(date);
    const hour = _time.split(':')[0];
    const minute = _time.split(':')[1];

    return this._formatTime(
      _date,
      parseInt(hour) + endTimeAdjustment,
      parseInt(minute)
    );
  };

  createMeetingSummary = (clientName: string | undefined) => {
    return `Meeting with ${clientName ? clientName : 'Client'}`;
  };

  createLocation = (type: string) => {
    const { t } = translations.global;
    return type !== '-' ? t(`client-view.task.interaction.type.${type}`) : '';
  };

  createDescription = (actions: string[]) => {
    const { t } = translations.global;
    const topics = actions.map(action =>
      t(`client-view.task.interaction.topics.${action}`)
    );
    return topics.length ? `Topics: ${topics.join(', ')}` : '';
  };

  createIcsData = () => {
    let content = 'BEGIN:VCALENDAR\r\n';
    content += 'BEGIN:VEVENT\r\n';
    content += 'VERSION:2.0\r\n';
    content += `SUMMARY:${this.summary}\r\n`;
    content += `DTSTART:${this.start}\r\n`;
    content += `DTEND:${this.end}\r\n`;
    content += `LOCATION:${this.location}\r\n`;
    content += `DESCRIPTION:${this.description}\r\n`;
    content += 'END:VEVENT\r\n';
    content += 'END:VCALENDAR';

    return content;
  };

  createDownloadUrl = () => {
    const blob = new Blob([this.createIcsData()], { type: 'text/calendar' });
    return window.URL.createObjectURL(blob);
  };
}
